import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { TypographyPageTitle } from '@seen/ui'
import { type JSX, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { Loader } from '#src/components/core/Loader.tsx'
import { AddDocumentButton } from '#src/components/document-reference/AddDocumentButton.tsx'
import { DocumentReferencesTable } from '#src/components/document-reference/DocumentReferencesTable.tsx'
import { useFolders } from '#src/components/document-reference/useFolders.ts'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function PatientDocumentsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  const participant = useCurrentParticipant()
  const folders = useFolders()

  const selectedFolderId = searchParams.get('folder') ?? undefined
  const initialFolder = folders.find((f) => f.id === selectedFolderId)

  const documentsQuery = useSearch(
    ExtendedDocumentReference,
    ExtendedDocumentReference.byParticipant({
      participant: participant.reference,
    })
  )

  const hasDocuments = documentsQuery.data?.length !== 0

  const handleDocumentSuccess = useCallback(
    (documentReference: ExtendedDocumentReference): void => {
      setSearchParams((prev) => {
        prev.set('documentReferenceId', documentReference.id)
        return prev
      })
    },
    [setSearchParams]
  )

  const renderContent = (): JSX.Element => {
    if (documentsQuery.isLoading) {
      return (
        <div className="flex justify-center">
          <Loader />
        </div>
      )
    }

    if (hasDocuments) {
      return <DocumentReferencesTable patient={participant.reference} />
    }

    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-gray-300">
        <div className="flex flex-col items-center gap-2">
          <p className="text-lg text-gray-500">No documents uploaded yet</p>
          <p className="text-gray-500 italic">
            Use the &ldquo;Add Document&rdquo; button above to upload your first
            document
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid">
      <div className="flex items-center justify-between gap-4">
        <TypographyPageTitle>Documents</TypographyPageTitle>
        <AddDocumentButton
          onSuccess={handleDocumentSuccess}
          initialFolder={initialFolder}
        />
      </div>
      {renderContent()}
    </div>
  )
}
